import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreation } from 'ahooks';
import { Typography } from '@mui/material';
import { Button, Tooltip } from '@mui/material';
import { FormText } from '@/components/form/form-text';
import { StepBox } from '@/components/layout/step-box';
import { Continue } from '@/components/ui/continue';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useMaskString } from '@/hooks/use-mask-string';
import { useRules } from '@/hooks/use-rules';
import { KoProtocol2 } from '../../../pages/register/components/ko-protocol/ko-protocol-2';
import { useAuthCode } from './use-authcode';

interface StepInputCodeProps {
  type: CodeType;
}

export const StepVerifyCode: React.FC<StepInputCodeProps> = ({ type }) => {
  const { t, i18n } = useTranslation();
  const { userName, mfa, authType, areaCode } = useBizState();
  const { maskString } = useMaskString();
  const rules = useRules();
  const {
    control,
    loading: verifyCodeRequesting,
    tooltipOpen,
    countdown,
    closeTooltip,
    submit,
    openTooltip,
    resend,
    resendLoading,
  } = useAuthCode(type);
  const [time, setTime] = useState(0);

  const account = 'email' === authType ? maskString(userName) : `${areaCode} ${maskString(userName)}`;
  const mfaDesc = useCreation(() => {
    return mfa?.mfaType === 2
      ? t('verification_code_instruction')
      : t('mfa_verification_instruction', {
          authType: mfa?.mfaType === 0 ? t('email') : t('phone'),
          userName: mfa?.userName
            ? mfa?.mfaType === 1
              ? ` ${mfa?.areaCode || ''} ${maskString(mfa.userName || '')}`
              : ` ${maskString(mfa.userName || '')}`
            : '',
        });
  }, [mfa, i18n.language]);

  const mapper = {
    register: {
      backFlow: Flow.REGISTER_STEP_INPUT_USERINFO,
      title: t('security_verification_title'),
      desc: t('verification_code_sent_message', { account: account }),
    },
    reset: {
      backFlow: Flow.RESET_PASSWORD_STEP_INPUT_USERNAME,
      title: t('security_verification_title'),
      desc: t('verification_code_sent_message', { account: account }),
    },
    recovery: {
      backFlow: Flow.RECOVERY_STEP_INPUT_USERNAME,
      title: t('security_verification_title'),
      desc: t('verification_code_sent_message', { account: account }),
    },
    mfa: {
      backFlow: Flow.LOGIN_STEP_INPUT_USERNAME,
      title: t('two_factor_auth_title'),
      desc: mfaDesc, // mfaType 0 - email, 1 - phone, 2 - authenticator
    },
    'enforced-mfa': {
      backFlow: Flow.LOGIN_STEP_INPUT_USERNAME,
      title: t('enforced_mfa_title'),
      text: t('enforced_mfa_description'),
      desc: mfaDesc, // mfaType 0 - email, 1 - phone, 2 - authenticator
      // mfaType 0 - email, 1 - phone, 2 - authenticator
      subText: t('enforced_mfa_subtext'),
    },
  };

  const { backFlow, title, desc, text, subText } = mapper[type] as (typeof mapper)[keyof typeof mapper] & {
    text?: string;
    subText?: string;
  };

  useEffect(() => {
    if (tooltipOpen) {
      setTime(countdown);
    } else {
      setTime(0);
    }
  }, [tooltipOpen]);

  return (
    <StepBox
      backFlow={backFlow}
      backButtonProps={{
        disabled: verifyCodeRequesting || resendLoading,
      }}
      title={title}
    >
      <Typography>{desc}</Typography>
      {text && <Typography>{text}</Typography>}
      {subText && <Typography>* {subText}</Typography>}
      <form className="flex flex-col gap-6" onSubmit={submit} noValidate>
        <FormText
          number
          name="authcode"
          label={t('enter_the_code')}
          control={control}
          rules={rules.authcode}
          slotProps={{ htmlInput: { maxLength: 6 } }}
        />

        {(type === 'mfa' || type === 'enforced-mfa') && mfa?.mfaType === 2 ? null : (
          <div className="flex items-center gap-2">
            <span>{t('code_not_received_prompt')}</span>
            <Tooltip
              title={t('resend_cooldown_message', { countdown })}
              open={tooltipOpen}
              onOpen={openTooltip}
              onClose={closeTooltip}
            >
              <Button
                size="small"
                onClick={resend}
                disabled={resendLoading}
                aria-label={time > 0 ? t('resend_cooldown_message', { countdown: time }) : undefined}
              >
                {t('resend')}
              </Button>
            </Tooltip>
          </div>
        )}

        {type === 'register' && i18n.language === 'ko' && <KoProtocol2 />}

        <Continue loading={verifyCodeRequesting} disabled={resendLoading} />
      </form>
    </StepBox>
  );
};
